import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ShoppingCart, Loader2, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchProduct, formatPrice, getProductTags, type ShopifyProduct, type ShopifyVariant } from '../utils/shopify'
import { useCartStore } from '../context/store'
import { useSeo, toPlainText } from '../hooks/useSeo'

export default function ProductPage() {
  const { handle } = useParams<{ handle: string }>()
  const [product, setProduct] = useState<ShopifyProduct | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<ShopifyVariant | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [activeImage, setActiveImage] = useState(0)
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState(false)

  const { addItem, openCart } = useCartStore()

  useSeo({
    title: product ? product.title : 'Product',
    description: product ? (toPlainText(product.body_html) || `${product.title} — premium custom-printed mouse mat by MatryxLab.`) : undefined,
    image: product?.images?.[0]?.src || null,
    path: `/products/${handle}`,
    type: 'product',
  })

  useEffect(() => {
    if (!handle) return
    setLoading(true)
    fetchProduct(handle)
      .then(p => {
        setProduct(p)
        setSelectedVariant(p.variants?.[0] || null)
      })
      .catch(() => setError('Product not found'))
      .finally(() => setLoading(false))
  }, [handle])

  const handleAddToCart = async () => {
    if (!selectedVariant) return
    setAdding(true)
    try {
      await addItem(selectedVariant.id, quantity)
      setAdded(true)
      setTimeout(() => setAdded(false), 2000)
    } catch (err) {
      console.error(err)
    } finally {
      setAdding(false)
    }
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="animate-spin text-brand-purple" size={32} />
    </div>
  )

  if (error || !product) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-brand-muted">{error || 'Product not found'}</p>
      <Link to="/catalogue" className="text-brand-purple hover:underline">Back to Catalogue</Link>
    </div>
  )

  const tags = getProductTags(product)
  const images = product.images || []
  const hasOptions = product.options && product.options.some(o => o.values.length > 1)

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Link to="/catalogue" className="inline-flex items-center gap-2 text-brand-muted hover:text-white mb-6 text-sm transition-colors">
          <ArrowLeft size={16} /> Back to Catalogue
        </Link>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Images */}
          <div>
            <div className="aspect-square overflow-hidden bg-brand-surface rounded-2xl relative">
              {images[activeImage] ? (
                <img src={images[activeImage].src} alt={product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl opacity-20">🖱️</div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={() => setActiveImage(i => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 rounded-full p-1.5 hover:bg-black/70 transition-colors">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={() => setActiveImage(i => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 rounded-full p-1.5 hover:bg-black/70 transition-colors">
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImage ? 'border-brand-purple' : 'border-transparent'}`}
                  >
                    <img src={img.src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tags.slice(0, 3).map(tag => (
                <span key={tag} className="text-xs bg-brand-purple/10 text-brand-purple px-2 py-0.5 rounded-full">{tag}</span>
              ))}
            </div>

            <h1 className="text-3xl font-bold mb-2">{product.title}</h1>

            <div className="text-3xl font-bold text-brand-purple mb-6">
              {selectedVariant?.price ? formatPrice(selectedVariant.price) : '—'}
            </div>

            {/* Variants */}
            {hasOptions && product.options?.map(option => (
              <div key={option.name} className="mb-4">
                <p className="text-sm font-medium mb-2">{option.name}</p>
                <div className="flex flex-wrap gap-2">
                  {option.values.map(value => {
                    const variant = product.variants?.find(v =>
                      v.option1 === value || v.option2 === value || v.option3 === value
                    )
                    const isSelected = selectedVariant?.option1 === value || selectedVariant?.option2 === value || selectedVariant?.option3 === value
                    return (
                      <button
                        key={value}
                        onClick={() => variant && setSelectedVariant(variant)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${isSelected ? 'bg-brand-purple border-brand-purple text-white' : 'border-white/10 hover:border-brand-purple/50'}`}
                      >
                        {value}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Quantity</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 rounded-lg bg-brand-surface border border-white/10 flex items-center justify-center hover:border-brand-purple/50 transition-colors">−</button>
                <span className="w-8 text-center font-medium">{quantity}</span>
                <button onClick={() => setQuantity(q => q + 1)} className="w-9 h-9 rounded-lg bg-brand-surface border border-white/10 flex items-center justify-center hover:border-brand-purple/50 transition-colors">+</button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={adding || !selectedVariant}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {adding ? <Loader2 size={18} className="animate-spin" /> : added ? <Check size={18} /> : <ShoppingCart size={18} />}
              {adding ? 'Adding...' : added ? 'Added to Cart!' : 'Add to Cart'}
            </button>

            {added && (
              <button onClick={openCart} className="w-full mt-2 btn-secondary py-2.5 text-sm">
                View Cart
              </button>
            )}

            {/* Description */}
            {product.body_html && (
              <div className="mt-8 pt-8 border-t border-white/10">
                <h2 className="font-semibold mb-3">Description</h2>
                <div
                  className="text-brand-muted text-sm leading-relaxed prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: product.body_html }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
